import { client } from '~publish/legacy/apollo-client'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import orgParser from '~publish/legacy/duplicate-server/parsers/orgParser'

import type { Organization } from './types'

/**
 * Account-actor query that replaces the legacy `organizations` RPC /
 * `GET /1/user/organizations.json` payload for Publish Redux bootstrap.
 * Field set mostly mirrors buffer-web CoreServicesClient::getOrganizationsForPublishUser
 * (CHAN-10784), except we omit contactAccountId (InternalService/BufferAdmin only)
 * and read account.email so isOwner can use public ownerEmail instead.
 * Keep account.id so Apollo can normalize Account and leave ROOT_QUERY.account
 * as a keyed ref for GetAccount / GetAccountWithOrganizations.
 */
export const GetLegacyOrganizations = graphql(/* GraphQL */ `
  query GetLegacyOrganizations {
    account {
      id
      email
      organizations {
        id
        name
        ownerEmail
        isOneBufferOrganization
        isCurrentOrganization
        allowedActions
        channelCount
        limits {
          channels
        }
        entitlements
        billing {
          __typename
          ... on OBBilling {
            subscription {
              plan {
                id
                name
              }
            }
          }
          ... on MPBilling {
            subscriptions {
              product
              plan
            }
          }
        }
      }
    }
  }
`)

type AccountOrganizations = NonNullable<
  ResultOf<typeof GetLegacyOrganizations>['account']
>
type GraphQLOrganization = AccountOrganizations['organizations'][number]

const MP_PRO_PLANS = new Set(['pro', 'awesome'])

function mapPlan(organization: GraphQLOrganization): {
  planName: string
  planBase: string
} {
  if (organization.isOneBufferOrganization) {
    if (organization.billing.__typename !== 'OBBilling') {
      return { planName: 'Free', planBase: 'one-buffer-free' }
    }
    const plan = organization.billing.subscription.plan
    return {
      planName: plan.name,
      planBase: `one-buffer-${plan.id}`,
    }
  }

  let planName = 'free'
  if (organization.billing.__typename === 'MPBilling') {
    for (const subscription of organization.billing.subscriptions) {
      if (subscription.product === 'publish') {
        planName = subscription.plan
        break
      }
    }
  }

  const planBase =
    planName === 'free'
      ? 'free'
      : MP_PRO_PLANS.has(planName)
        ? 'pro'
        : 'business'

  return { planName, planBase }
}

/**
 * Maps a GraphQL Organization (+ account email) onto the Core REST shape that
 * orgParser already consumes after CHAN-10784 / buffer-web #20947.
 *
 * isOwner is derived from ownerEmail (public) matching the signed-in account
 * email. Do not use contactAccountId: it is @requireActor(InternalService,
 * BufferAdmin) and FORBIDDEN for normal Publish users, which nulls the whole
 * GetLegacyOrganizations response.
 */
export function mapGraphQLOrganizationToLegacy(
  organization: GraphQLOrganization,
  accountEmail: string,
): Organization {
  const locked = !organization.allowedActions.includes('manageChannelGroups')
  const { planName, planBase } = mapPlan(organization)

  return orgParser({
    id: organization.id,
    locked,
    selected: organization.isCurrentOrganization,
    name: organization.name,
    ownerEmail: organization.ownerEmail,
    planName,
    planBase,
    profileLimit: organization.limits.channels,
    profilesCount: organization.channelCount,
    canManageChannels: organization.allowedActions.includes('manageChannels'),
    isOwner: organization.ownerEmail === accountEmail,
    isOneBufferOrganization: organization.isOneBufferOrganization,
    entitlements: [...organization.entitlements],
  })
}

export function mapGraphQLOrganizationsToLegacy(
  account: AccountOrganizations,
): Organization[] {
  return account.organizations.map((organization) =>
    mapGraphQLOrganizationToLegacy(organization, account.email),
  )
}

export async function fetchLegacyOrganizations(): Promise<Organization[]> {
  const { data } = await client.query({
    query: GetLegacyOrganizations,
    // Always hit the network: INIT_ORGANIZATIONS also runs after billing
    // updates, when a stale Apollo cache would leave Redux out of date.
    fetchPolicy: 'network-only',
    // Billing refresh must not join an in-flight bootstrap query.
    context: { queryDeduplication: false },
  })

  const account = data?.account
  if (!account) {
    throw new Error('GetLegacyOrganizations returned no account')
  }

  return mapGraphQLOrganizationsToLegacy(account)
}
